import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AppointmentList } from '@/components/dashboard/AppointmentList';
import { CoachAvailabilityList } from '@/components/dashboard/CoachAvailabilityList';
import { CoachAvailabilityDashboard } from '@/components/dashboard/CoachAvailabilityDashboard';
import { CustomerSearch } from '@/components/dashboard/CustomerSearch';
import { ManualSchedulingDialog } from '@/components/dashboard/ManualSchedulingDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarCheck, Users } from 'lucide-react';
import { CustomerExamList } from '@/components/examiner/CustomerExamList';

export function CoachDashboard() {
  const { t, dir } = useLanguage();
  const { hasRole, currentUser } = useAuth();
  const isAdmin = hasRole('admin');
  const [showManualScheduling, setShowManualScheduling] = useState(false);

  return (
    <div className="space-y-4 md:space-y-6 lg:space-y-8" dir={dir}>
      {/* Header - More compact on mobile */}
      <div className="flex flex-col gap-1 md:gap-2">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">{t('dashboard.coach.title')}</h2>
        <p className="text-xs md:text-sm lg:text-base text-muted-foreground">
          {t('dashboard.coach.description')}
        </p>
      </div>

      {/* Main Grid - Stack on mobile, 2 columns on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
        {/* Left Column: Clinic, Queue & Statistics */}
        <div className="space-y-4 md:space-y-6">
          {/* Clinic Dashboard */}
          <CoachAvailabilityDashboard />
        </div>

        {/* Right Column: Search, Appointments & Schedules */}
        <div className="space-y-4 md:space-y-6">
          {/* Customer Search for Admin Users */}
          <CustomerSearch isAdmin={isAdmin} />

          {/* Appointments Section */}
          <Card className="overflow-hidden">
            <CardHeader className="p-3 md:p-4 lg:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div className="flex items-center gap-2">
                  <CalendarCheck className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                  <CardTitle className="text-base md:text-lg">{t('dashboard.coach.appointments.title')}</CardTitle>
                </div>
                {isAdmin && (
                  <Button 
                    onClick={() => setShowManualScheduling(true)}
                    className="w-full sm:w-auto text-sm h-9"
                    size="sm"
                  >
                    <CalendarCheck className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    <span className="hidden sm:inline">{t('dashboard.admin.appointments.createAppointment')}</span>
                    <span className="sm:hidden">Create</span>
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
              <AppointmentList />
            </CardContent>
          </Card>

          {/* Schedule/Availability Section */}
          <CoachAvailabilityList />
        </div>
      </div>

      {/* Clinical beneficiaries table: scale and chosen indicators (#93) */}
      <Card className="overflow-hidden">
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
            <CardTitle className="text-base md:text-lg">{t('dashboard.coach.beneficiaries.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
          <CustomerExamList allOrganizations />
        </CardContent>
      </Card>

      {/* Manual Scheduling Dialog for Admin Users */}
      {isAdmin && (
        <ManualSchedulingDialog
          open={showManualScheduling}
          onOpenChange={setShowManualScheduling}
          currentCoachId={currentUser?.uid}
        />
      )}
    </div>
  );
}